import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  CheckSquare, 
  Square, 
  Edit3, 
  Trash2, 
  Download, 
  Mail, 
  Printer,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { RestaurantOrder } from '@shared/schema';

interface BulkOperationsProps {
  selectedOrders: number[];
  onSelectionChange: (orderIds: number[]) => void;
  orders: RestaurantOrder[];
  onBulkStatusUpdate: (orderIds: number[], status: string) => Promise<void>;
  onBulkDelete: (orderIds: number[]) => Promise<void>;
  onBulkExport: (orderIds: number[], format: string) => void;
}

function parseOrderItems(orderItems: string | null | undefined): any[] {
  try {
    return JSON.parse(orderItems || '[]');
  } catch {
    return [];
  }
}

export function BulkOperations({
  selectedOrders,
  onSelectionChange,
  orders,
  onBulkStatusUpdate,
  onBulkDelete,
  onBulkExport
}: BulkOperationsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const { toast } = useToast();

  const selectedOrderObjects = orders.filter(order => selectedOrders.includes(order.id));
  const allOrderIds = orders.map(order => order.id);
  const isAllSelected = selectedOrders.length === orders.length && orders.length > 0;
  const isIndeterminate = selectedOrders.length > 0 && selectedOrders.length < orders.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(allOrderIds);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedOrders.length === 0) return;
    
    setIsLoading(true);
    try {
      await onBulkStatusUpdate(selectedOrders, bulkStatus);
      toast({
        title: 'Success',
        description: `Updated ${selectedOrders.length} orders to ${bulkStatus}`
      });
      onSelectionChange([]);
      setBulkStatus('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update orders',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) return;
    
    setIsLoading(true);
    try {
      await onBulkDelete(selectedOrders);
      toast({
        title: 'Success',
        description: `Deleted ${selectedOrders.length} orders`
      });
      onSelectionChange([]);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete orders',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusCounts = () => {
    const counts = selectedOrderObjects.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(counts).map(([status, count]) => ({ status, count }));
  };

  const getTotalValue = () => {
    return selectedOrderObjects.reduce((sum, order) => sum + order.totalAmount, 0);
  };

  if (selectedOrders.length === 0) {
    return (
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={isAllSelected}
              ref={ref => {
                if (ref) ref.indeterminate = isIndeterminate;
              }}
              onCheckedChange={handleSelectAll}
            />
            <span className="text-sm text-gray-600">
              Select orders to enable bulk operations
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4 border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={isAllSelected}
              ref={ref => {
                if (ref) ref.indeterminate = isIndeterminate;
              }}
              onCheckedChange={handleSelectAll}
            />
            <span>
              {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected
            </span>
            <Badge variant="secondary">
              Total: ${getTotalValue().toFixed(2)}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onSelectionChange([])}
          >
            <X className="w-4 h-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Overview */}
        <div className="flex flex-wrap gap-2">
          {getStatusCounts().map(({ status, count }) => (
            <Badge key={status} variant="outline" className="capitalize">
              {status}: {count}
            </Badge>
          ))}
        </div>

        <Separator />

        {/* Bulk Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Update */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Update Status</label>
            <div className="flex gap-2">
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                size="sm" 
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatus || isLoading}
              >
                <Edit3 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Export Options */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Export</label>
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onBulkExport(selectedOrders, 'csv')}
                className="flex-1"
              >
                CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onBulkExport(selectedOrders, 'xlsx')}
                className="flex-1"
              >
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onBulkExport(selectedOrders, 'pdf')}
                className="flex-1"
              >
                PDF
              </Button>
            </div>
          </div>

          {/* Communication */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Communication</label>
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  if (!printWindow) return;
                  
                  printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>Orders - Bulk Print</title>
                      <style>
                        body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
                        .order-page { page-break-after: always; padding: 20px; }
                        .order-page:last-child { page-break-after: auto; }
                        h1, h2 { margin: 10px 0; }
                        .order-header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                        .info-section { margin-bottom: 20px; }
                        .info-row { display: flex; justify-content: space-between; margin: 5px 0; }
                        .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                        .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        .items-table th { background-color: #f4f4f4; }
                        .total-section { border-top: 2px solid #333; padding-top: 10px; margin-top: 20px; }
                        .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: bold; }
                        @media print { 
                          body { margin: 0; }
                          .order-page { page-break-after: always; }
                        }
                      </style>
                    </head>
                    <body>
                      ${selectedOrderObjects.map(order => {
                        const orderItems = parseOrderItems(order.orderItems);
                        return `
                          <div class="order-page">
                            <div class="order-header">
                              <h1>Order ${order.orderNumber}</h1>
                              <div>Status: ${order.status}</div>
                            </div>
                            
                            <div class="info-section">
                              <h2>Restaurant Information</h2>
                              <div class="info-row"><span>Restaurant:</span><span>${order.restaurantName}</span></div>
                              <div class="info-row"><span>Contact:</span><span>${order.contactPerson}</span></div>
                              <div class="info-row"><span>Phone:</span><span>${order.phoneNumber}</span></div>
                              <div class="info-row"><span>Email:</span><span>${order.email}</span></div>
                            </div>
                            
                            <div class="info-section">
                              <h2>Delivery Information</h2>
                              <div class="info-row"><span>Address:</span><span>${order.deliveryAddress}</span></div>
                              <div class="info-row"><span>Date:</span><span>${new Date(order.deliveryDate).toLocaleDateString()}</span></div>
                              <div class="info-row"><span>Time:</span><span>${order.deliveryTime}</span></div>
                              ${order.deliveryInstructions ? `<div class="info-row"><span>Instructions:</span><span>${order.deliveryInstructions}</span></div>` : ''}
                            </div>
                            
                            <h2>Order Items</h2>
                            <table class="items-table">
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Quantity</th>
                                  <th>Unit Price</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                ${orderItems.map((item: any) => `
                                  <tr>
                                    <td>${item.name}</td>
                                    <td>${item.quantity} ${item.unit}</td>
                                    <td>$${item.pricePerUnit.toFixed(2)}</td>
                                    <td>$${(item.quantity * item.pricePerUnit).toFixed(2)}</td>
                                  </tr>
                                `).join('')}
                              </tbody>
                            </table>
                            
                            <div class="total-section">
                              <div class="info-row"><span>Subtotal:</span><span>$${order.subtotalAmount.toFixed(2)}</span></div>
                              ${order.discountAmount > 0 ? `<div class="info-row"><span>Discount (${order.discountCode}):</span><span>-$${order.discountAmount.toFixed(2)}</span></div>` : ''}
                              <div class="total-row"><span>Total:</span><span>$${order.totalAmount.toFixed(2)}</span></div>
                            </div>
                          </div>
                        `;
                      }).join('')}
                      <script>window.print(); setTimeout(() => window.close(), 100);</script>
                    </body>
                    </html>
                  `);
                  printWindow.document.close();
                }}
                className="flex-1"
              >
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-red-600">Danger Zone</label>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  disabled={isLoading}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    Confirm Bulk Delete
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <p>Are you sure you want to delete {selectedOrders.length} orders? 
                      This action cannot be undone.</p>
                      
                      <div className="mt-3 p-3 bg-red-50 rounded border">
                        <div className="text-sm font-medium">Orders to be deleted:</div>
                        <ul className="text-sm text-gray-600 mt-1">
                          {selectedOrderObjects.slice(0, 5).map(order => (
                            <li key={order.id}>• {order.orderNumber} - {order.restaurantName}</li>
                          ))}
                          {selectedOrderObjects.length > 5 && (
                            <li>• ... and {selectedOrderObjects.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete {selectedOrders.length} Orders
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
        </div>
      </CardContent>
    </Card>
  );
}